/**
 * useCustomForms Hook
 *
 * Phase 6.6: User-Uploaded Forms
 *
 * React Query hook for CRUD operations on contractor-owned custom forms.
 * Provides form upload, AI analysis, review, and fill functionality.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { functions, storage } from '@/lib/firebase';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { ParsedFormField } from '@/types/smart-schema';

// =============================================================================
// TYPES
// =============================================================================

export type AIAnalysisStatus = 'pending' | 'analyzing' | 'completed' | 'failed';
export type ReviewStatus = 'pending' | 'in_progress' | 'approved' | 'rejected';

export interface CustomForm {
  id: string;
  contractor_id: string;

  // Form info
  form_name: string;
  description: string | null;
  jurisdiction_code: string | null;
  permit_type: string | null;

  // Storage
  pdf_storage_path: string;
  pdf_filename: string;
  pdf_file_size: number | null;
  pdf_page_count: number | null;

  // Schema
  fields: ParsedFormField[];
  sections: Array<{ id: string; name: string; order: number; fields: string[] }>;

  // AI Analysis
  ai_analysis_status: AIAnalysisStatus;
  ai_confidence_avg: number | null;
  ai_model_version: string | null;
  ai_analyzed_at: string | null;
  ai_error_message: string | null;

  // Review
  review_status: ReviewStatus;
  reviewed_at: string | null;
  review_notes: string | null;
  fields_reviewed: number;
  fields_corrected: number;

  // Usage
  times_used: number;
  last_used_at: string | null;

  // Status
  is_active: boolean;
  is_favorite: boolean;

  // Timestamps
  created_at: string;
  updated_at: string;

  // Organization
  tags: string[];
  metadata: Record<string, unknown>;
}

export interface CreateCustomFormInput {
  form_name: string;
  description?: string;
  jurisdiction_code?: string;
  permit_type?: string;
  pdf_file: File;
  tags?: string[];
}

export interface UpdateCustomFormInput {
  form_name?: string;
  description?: string;
  jurisdiction_code?: string;
  permit_type?: string;
  fields?: ParsedFormField[];
  sections?: CustomForm['sections'];
  review_status?: ReviewStatus;
  review_notes?: string;
  is_favorite?: boolean;
  tags?: string[];
}

export interface CustomFormFilters {
  review_status?: ReviewStatus;
  ai_analysis_status?: AIAnalysisStatus;
  is_favorite?: boolean;
  jurisdiction_code?: string;
  search?: string;
}

// =============================================================================
// QUERY KEYS
// =============================================================================

export const customFormKeys = {
  all: ['custom-forms'] as const,
  lists: () => [...customFormKeys.all, 'list'] as const,
  list: (filters: CustomFormFilters) => [...customFormKeys.lists(), filters] as const,
  details: () => [...customFormKeys.all, 'detail'] as const,
  detail: (id: string) => [...customFormKeys.details(), id] as const,
  recent: () => [...customFormKeys.all, 'recent'] as const,
  needingReview: () => [...customFormKeys.all, 'needing-review'] as const,
  stats: () => [...customFormKeys.all, 'stats'] as const,
};

// =============================================================================
// FETCH FUNCTIONS
// =============================================================================

async function fetchCustomForms(
  contractorId: string,
  filters: CustomFormFilters = {}
): Promise<CustomForm[]> {
  let query = supabase
    .from('custom_forms')
    .select('*')
    .eq('contractor_id', contractorId)
    .eq('is_active', true)
    .order('updated_at', { ascending: false });

  if (filters.review_status) {
    query = query.eq('review_status', filters.review_status);
  }
  if (filters.ai_analysis_status) {
    query = query.eq('ai_analysis_status', filters.ai_analysis_status);
  }
  if (filters.is_favorite !== undefined) {
    query = query.eq('is_favorite', filters.is_favorite);
  }
  if (filters.jurisdiction_code) {
    query = query.eq('jurisdiction_code', filters.jurisdiction_code);
  }
  if (filters.search) {
    query = query.ilike('form_name', `%${filters.search}%`);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data as CustomForm[];
}

async function fetchCustomFormById(id: string): Promise<CustomForm | null> {
  const { data, error } = await supabase
    .from('custom_forms')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data as CustomForm;
}

async function fetchRecentForms(contractorId: string, limit = 10): Promise<CustomForm[]> {
  const { data, error } = await supabase.rpc('get_contractor_recent_forms', {
    p_contractor_id: contractorId,
    p_limit: limit,
  });

  if (error) throw error;
  return data as CustomForm[];
}

async function fetchFormsNeedingReview(contractorId: string): Promise<CustomForm[]> {
  const { data, error } = await supabase.rpc('get_contractor_forms_needing_review', {
    p_contractor_id: contractorId,
  });

  if (error) throw error;
  return data as CustomForm[];
}

async function fetchFormStats(contractorId: string): Promise<{
  total: number;
  approved: number;
  pending_review: number;
  analyzing: number;
  favorites: number;
}> {
  const { data, error } = await supabase
    .from('custom_forms')
    .select('review_status, ai_analysis_status, is_favorite')
    .eq('contractor_id', contractorId)
    .eq('is_active', true);

  if (error) throw error;

  const forms = data || [];
  return {
    total: forms.length,
    approved: forms.filter(f => f.review_status === 'approved').length,
    pending_review: forms.filter(f =>
      f.ai_analysis_status === 'completed' &&
      ['pending', 'in_progress'].includes(f.review_status)
    ).length,
    analyzing: forms.filter(f => f.ai_analysis_status === 'analyzing').length,
    favorites: forms.filter(f => f.is_favorite).length,
  };
}

// =============================================================================
// MUTATION FUNCTIONS
// =============================================================================

async function uploadCustomForm(
  contractorId: string,
  input: CreateCustomFormInput
): Promise<CustomForm> {
  // 1. Generate form ID
  const formId = crypto.randomUUID();

  // 2. Upload PDF to Firebase Storage
  const storagePath = `custom-forms/${contractorId}/${formId}.pdf`;
  const storageRef = ref(storage, storagePath);

  const uploadResult = await uploadBytes(storageRef, input.pdf_file, {
    contentType: 'application/pdf',
    customMetadata: {
      originalFilename: input.pdf_file.name,
      contractorId,
      formName: input.form_name,
    },
  });

  // 3. Create database record
  const { data, error } = await supabase
    .from('custom_forms')
    .insert({
      id: formId,
      contractor_id: contractorId,
      form_name: input.form_name,
      description: input.description || null,
      jurisdiction_code: input.jurisdiction_code || null,
      permit_type: input.permit_type || null,
      pdf_storage_path: storagePath,
      pdf_filename: input.pdf_file.name,
      pdf_file_size: input.pdf_file.size,
      tags: input.tags || [],
      ai_analysis_status: 'pending',
      review_status: 'pending',
    })
    .select()
    .single();

  if (error) {
    // Cleanup: delete uploaded file on DB error
    try {
      await deleteObject(storageRef);
    } catch (cleanupError) {
      console.error('Failed to cleanup uploaded file:', cleanupError);
    }
    throw error;
  }

  return data as CustomForm;
}

async function triggerFormAnalysis(formId: string): Promise<void> {
  // Update status to analyzing
  await supabase
    .from('custom_forms')
    .update({ ai_analysis_status: 'analyzing' })
    .eq('id', formId);

  // Call Cloud Function to analyze
  const analyzeCustomForm = httpsCallable(functions, 'analyzeCustomForm');
  await analyzeCustomForm({ formId });
}

async function updateCustomForm(
  id: string,
  input: UpdateCustomFormInput
): Promise<CustomForm> {
  const updateData: Record<string, unknown> = {};

  if (input.form_name !== undefined) updateData.form_name = input.form_name;
  if (input.description !== undefined) updateData.description = input.description;
  if (input.jurisdiction_code !== undefined) updateData.jurisdiction_code = input.jurisdiction_code;
  if (input.permit_type !== undefined) updateData.permit_type = input.permit_type;
  if (input.is_favorite !== undefined) updateData.is_favorite = input.is_favorite;
  if (input.tags !== undefined) updateData.tags = input.tags;
  if (input.review_notes !== undefined) updateData.review_notes = input.review_notes;

  if (input.fields !== undefined) {
    updateData.fields = input.fields;
    // Recalculate confidence
    updateData.ai_confidence_avg = input.fields.length > 0
      ? input.fields.reduce((sum, f) => sum + f.confidence, 0) / input.fields.length
      : 0;
  }

  if (input.sections !== undefined) {
    updateData.sections = input.sections;
  }

  if (input.review_status !== undefined) {
    updateData.review_status = input.review_status;
    if (input.review_status === 'approved') {
      updateData.reviewed_at = new Date().toISOString();
    }
  }

  const { data, error } = await supabase
    .from('custom_forms')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as CustomForm;
}

async function deleteCustomForm(id: string, storagePath: string): Promise<void> {
  // 1. Delete from database (soft delete)
  const { error } = await supabase
    .from('custom_forms')
    .update({ is_active: false })
    .eq('id', id);

  if (error) throw error;

  // 2. Optionally delete from storage (or keep for recovery)
  // For now, we soft-delete only
}

async function updateFieldMapping(
  formId: string,
  fieldId: string,
  updates: Partial<ParsedFormField>
): Promise<CustomForm> {
  // Fetch current form
  const form = await fetchCustomFormById(formId);
  if (!form) throw new Error('Form not found');

  // Find and update field
  const fieldIndex = form.fields.findIndex(f => f.pdfField === fieldId);
  if (fieldIndex === -1) throw new Error('Field not found');

  const oldField = form.fields[fieldIndex];
  const newField = { ...oldField, ...updates };
  const newFields = [...form.fields];
  newFields[fieldIndex] = newField;

  // Track correction count
  const wasChanged = updates.semanticId !== undefined && updates.semanticId !== oldField.semanticId;

  const updateData: Record<string, unknown> = {
    fields: newFields,
    fields_reviewed: form.fields_reviewed + 1,
    ai_confidence_avg: newFields.reduce((sum, f) => sum + f.confidence, 0) / newFields.length,
  };

  if (wasChanged) {
    updateData.fields_corrected = form.fields_corrected + 1;
  }

  // Mark as in_progress if pending
  if (form.review_status === 'pending') {
    updateData.review_status = 'in_progress';
  }

  const { data, error } = await supabase
    .from('custom_forms')
    .update(updateData)
    .eq('id', formId)
    .select()
    .single();

  if (error) throw error;
  return data as CustomForm;
}

async function approveCustomForm(formId: string): Promise<CustomForm> {
  const { data, error } = await supabase
    .from('custom_forms')
    .update({
      review_status: 'approved',
      reviewed_at: new Date().toISOString(),
    })
    .eq('id', formId)
    .select()
    .single();

  if (error) throw error;
  return data as CustomForm;
}

async function recordFormUsage(
  formId: string,
  contractorId: string,
  jobId?: string,
  fillStats?: { fieldsFilled: number; fieldsTotal: number }
): Promise<void> {
  // Insert fill record
  await supabase.from('custom_form_fills').insert({
    custom_form_id: formId,
    contractor_id: contractorId,
    job_id: jobId || null,
    fields_filled: fillStats?.fieldsFilled || 0,
    fields_total: fillStats?.fieldsTotal || 0,
    completeness_percent: fillStats
      ? (fillStats.fieldsFilled / fillStats.fieldsTotal) * 100
      : 0,
  });

  // Increment usage count
  await supabase.rpc('increment_custom_form_usage', { form_id: formId });
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Fetch all custom forms for the current contractor
 */
export function useCustomForms(filters: CustomFormFilters = {}) {
  const { user } = useAuth();

  return useQuery({
    queryKey: customFormKeys.list(filters),
    queryFn: () => fetchCustomForms(user!.uid, filters),
    enabled: !!user,
  });
}

/**
 * Fetch a single custom form by ID
 */
export function useCustomForm(id: string | undefined) {
  return useQuery({
    queryKey: customFormKeys.detail(id || ''),
    queryFn: () => fetchCustomFormById(id!),
    enabled: !!id,
  });
}

/**
 * Fetch recent forms for quick access
 */
export function useRecentCustomForms(limit = 10) {
  const { user } = useAuth();

  return useQuery({
    queryKey: customFormKeys.recent(),
    queryFn: () => fetchRecentForms(user!.uid, limit),
    enabled: !!user,
  });
}

/**
 * Fetch forms needing review
 */
export function useCustomFormsNeedingReview() {
  const { user } = useAuth();

  return useQuery({
    queryKey: customFormKeys.needingReview(),
    queryFn: () => fetchFormsNeedingReview(user!.uid),
    enabled: !!user,
  });
}

/**
 * Fetch form statistics
 */
export function useCustomFormStats() {
  const { user } = useAuth();

  return useQuery({
    queryKey: customFormKeys.stats(),
    queryFn: () => fetchFormStats(user!.uid),
    enabled: !!user,
  });
}

/**
 * Upload a new custom form
 */
export function useUploadCustomForm() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (input: CreateCustomFormInput) =>
      uploadCustomForm(user!.uid, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customFormKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customFormKeys.stats() });
    },
  });
}

/**
 * Trigger AI analysis for a form
 */
export function useTriggerFormAnalysis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: triggerFormAnalysis,
    onSuccess: (_, formId) => {
      queryClient.invalidateQueries({ queryKey: customFormKeys.detail(formId) });
      queryClient.invalidateQueries({ queryKey: customFormKeys.lists() });
    },
  });
}

/**
 * Update a custom form
 */
export function useUpdateCustomForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCustomFormInput }) =>
      updateCustomForm(id, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: customFormKeys.lists() });
      queryClient.setQueryData(customFormKeys.detail(data.id), data);
    },
  });
}

/**
 * Delete a custom form (soft delete)
 */
export function useDeleteCustomForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, storagePath }: { id: string; storagePath: string }) =>
      deleteCustomForm(id, storagePath),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customFormKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customFormKeys.stats() });
    },
  });
}

/**
 * Update a single field mapping
 */
export function useUpdateFieldMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      formId,
      fieldId,
      updates,
    }: {
      formId: string;
      fieldId: string;
      updates: Partial<ParsedFormField>;
    }) => updateFieldMapping(formId, fieldId, updates),
    onSuccess: (data) => {
      queryClient.setQueryData(customFormKeys.detail(data.id), data);
      queryClient.invalidateQueries({ queryKey: customFormKeys.needingReview() });
    },
  });
}

/**
 * Approve a custom form after review
 */
export function useApproveCustomForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: approveCustomForm,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: customFormKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customFormKeys.needingReview() });
      queryClient.invalidateQueries({ queryKey: customFormKeys.stats() });
      queryClient.setQueryData(customFormKeys.detail(data.id), data);
    },
  });
}

/**
 * Record form usage for analytics
 */
export function useRecordFormUsage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      formId,
      jobId,
      fillStats,
    }: {
      formId: string;
      jobId?: string;
      fillStats?: { fieldsFilled: number; fieldsTotal: number };
    }) => recordFormUsage(formId, user!.uid, jobId, fillStats),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customFormKeys.recent() });
    },
  });
}

/**
 * Get PDF download URL for a custom form
 */
export async function getCustomFormPdfUrl(storagePath: string): Promise<string> {
  const storageRef = ref(storage, storagePath);
  return getDownloadURL(storageRef);
}
